import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs, addDoc, serverTimestamp, query, where, Timestamp, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { createSubscription } from "../../services/subscriptionService";
import { processSubscriptionPayment } from "../../services/paymentService";
import { PaymentMethod, SubscriptionTier } from "../../types/payment";
import { SUBSCRIPTION_PLANS } from "../../config/subscriptionPlans";
import { CreditCard, Lock, CheckCircle, AlertCircle, ArrowLeft, Loader2, Tag, Check, X, Upload, Clock, Copy, Calendar, Mail, Sparkles } from "lucide-react";
import AuthModal from "../../components/AuthModal";
import SEO from "../../components/SEO";

export default function SubscriptionCheckout() {
  const [searchParams] = useSearchParams();
  const tier = searchParams.get("tier") as SubscriptionTier;
  const { user } = useAuthContext();
  const navigate = useNavigate();

  // Redirect if trying to access yearly plans
  useEffect(() => {
    if (tier === "pro_yearly" || tier === "pro_plus_yearly") {
      navigate("/subscription/renew");
    }
  }, [tier, navigate]);

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  
  // Payment states
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'uploaded' | 'verifying' | 'success'>('pending');
  const [copied, setCopied] = useState(false);
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payerEmail, setPayerEmail] = useState("");
  const [payerName, setPayerName] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | null>(null);
  const [paymentSettings, setPaymentSettings] = useState<{
    promptPayNumber: string;
    accountName: string;
    qrCodeUrl: string;
  } | null>(null);

  const PROMPTPAY_NUMBER = paymentSettings?.promptPayNumber || '0812345678';
  const PROMPTPAY_NAME = paymentSettings?.accountName || 'EduMi Co., Ltd.';
  
  // Discount states
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  const [discountError, setDiscountError] = useState("");
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);

  const plan = SUBSCRIPTION_PLANS.find((p) => p.tier === tier);
  
  const calculatePrice = () => {
    if (!plan) return 0;
    const monthlyPrice = plan.price;
    if (billingPeriod === 'yearly') {
      return monthlyPrice * 10;
    }
    return monthlyPrice;
  };
  
  const [finalPrice, setFinalPrice] = useState(calculatePrice());

  // Auto-fill email from logged-in user
  useEffect(() => {
    if (user?.email) {
      setPayerEmail(user.email);
    }
  }, [user]);

  // Load payment settings
  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'paymentSettings', 'default'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setPaymentSettings({
          promptPayNumber: data.promptpay?.id || '0812345678',
          accountName: data.promptpay?.name || 'EduMi Co., Ltd.',
          qrCodeUrl: data.promptpay?.qrCodeUrl || ''
        });
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  useEffect(() => {
    if (plan) {
      const basePrice = calculatePrice();
      const discount = appliedDiscount 
        ? appliedDiscount.type === "percentage"
          ? Math.round(basePrice * appliedDiscount.value / 100)
          : appliedDiscount.value
        : 0;
      setFinalPrice(basePrice - discount);
      setDiscountAmount(discount);
    }
  }, [plan, billingPeriod, appliedDiscount]);
  
  useEffect(() => {
    if (showPaymentForm && !paymentRef) {
      const expiry = new Date(Date.now() + 30 * 60 * 1000);
      setExpiryTime(expiry);
      const ref = `SUB${Date.now()}${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
      setPaymentRef(ref);
    }
  }, [showPaymentForm, paymentRef]);

  useEffect(() => {
    if (!expiryTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = expiryTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        setTimeRemaining('หมดเวลา');
        clearInterval(interval);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryTime]);


  // Apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError("กรุณากรอกรหัสส่วนลด");
      return;
    }

    setCheckingDiscount(true);
    setDiscountError("");

    try {
      // Query discount from global subscription discounts
      const discountQuery = query(
        collection(db, "subscription_discounts"),
        where("code", "==", discountCode.trim().toUpperCase())
      );
      const discountSnap = await getDocs(discountQuery);

      if (discountSnap.empty) {
        setDiscountError("ไม่พบรหัสส่วนลดนี้");
        return;
      }

      const discountDoc = discountSnap.docs[0];
      const discount = { id: discountDoc.id, ...discountDoc.data() };

      // Check if active
      if (!discount.isActive) {
        setDiscountError("รหัสส่วนลดนี้ถูกปิดใช้งานแล้ว");
        return;
      }

      // Check expiration
      if (discount.expiresAt) {
        const expireDate = discount.expiresAt?.toDate?.() || new Date(discount.expiresAt);
        if (expireDate < new Date()) {
          setDiscountError("รหัสส่วนลดนี้หมดอายุแล้ว");
          return;
        }
      }

      // Check if applicable to this tier
      if (discount.applicableTiers && !discount.applicableTiers.includes(tier)) {
        setDiscountError("รหัสส่วนลดนี้ไม่สามารถใช้กับแพ็กเกจนี้ได้");
        return;
      }

      // Calculate discount amount based on billing period
      const basePrice = calculatePrice();
      let discountValue = 0;
      if (discount.type === "percentage") {
        discountValue = Math.round((basePrice * discount.value) / 100);
      } else {
        discountValue = discount.value;
      }

      // Ensure discount doesn't exceed plan price
      discountValue = Math.min(discountValue, basePrice);

      setAppliedDiscount(discount);
      setDiscountAmount(discountValue);
      setFinalPrice(basePrice - discountValue);
    } catch (error) {
      console.error("Error applying discount:", error);
      setDiscountError("เกิดข้อผิดพลาดในการตรวจสอบรหัสส่วนลด");
    } finally {
      setCheckingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountAmount(0);
    setFinalPrice(calculatePrice());
    setDiscountError("");
  };

  const handleSlipUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('ไฟล์ใหญ่เกิน 5MB');
      return;
    }

    setSlipFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setSlipPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generatePromptPayQR = () => {
    // If custom QR code is uploaded, use it
    if (paymentSettings?.qrCodeUrl) {
      return paymentSettings.qrCodeUrl;
    }
    // Otherwise generate from PromptPay API
    return `https://promptpay.io/${PROMPTPAY_NUMBER}/${finalPrice}.png`;
  };

  const handleProceedToPayment = () => {
    setShowPaymentForm(true);
  };


  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-textDark">ไม่พบแพ็กเกจที่เลือก</p>
        </div>
      </div>
    );
  }

  const handleSubmitPayment = async () => {
    if (!slipFile || !tier) {
      alert('กรุณาอัปโหลดสลิปการโอนเงิน');
      return;
    }

    if (!payerEmail || !payerEmail.includes('@')) {
      alert('กรุณากรอกอีเมลให้ถูกต้อง');
      return;
    }

    setIsUploading(true);
    setPaymentStatus('verifying');

    try {
      console.log('[Payment] Uploading subscription slip to R2...');
      
      // For subscription (guest users), we need to handle upload differently
      // Use a temporary token or public upload endpoint
      const tempId = `temp_${Date.now()}`;
      const fileName = `payment-slips/subscription/${tempId}/${Date.now()}_${slipFile.name}`;
      
      const formData = new FormData();
      formData.append('file', slipFile);
      formData.append('fileName', fileName);
      
      // If user is logged in, use their token
      let headers: HeadersInit = {};
      if (user) {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const idToken = await currentUser.getIdToken(true);
          headers = {
            'Authorization': `Bearer ${idToken}`,
          };
        }
      }
      
      const uploadResponse = await fetch('https://edumi-upload-api.edumi-subdomin.workers.dev', {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('[Payment] Upload error:', errorText);
        throw new Error('ไม่สามารถอัปโหลดสลิปได้');
      }

      const uploadResult = await uploadResponse.json();
      const slipUrl = uploadResult.publicUrl;
      
      console.log('[Payment] Subscription slip uploaded successfully:', slipUrl);

      const paymentRecord = {
        userId: user?.uid || null, // Will be linked after registration if guest
        email: payerEmail.toLowerCase().trim(),
        userName: payerName || payerEmail,
        type: 'subscription',
        tier: tier,
        planName: plan!.name,
        amount: finalPrice,
        originalAmount: calculatePrice(),
        discountAmount: discountAmount,
        discountCode: appliedDiscount?.code || null,
        billingPeriod: billingPeriod,
        status: 'pending',
        paymentMethod: 'promptpay',
        promptPayRef: paymentRef,
        slipImageUrl: slipUrl,
        createdAt: Timestamp.now(),
        verifiedAt: null,
        expiresAt: Timestamp.fromDate(expiryTime!),
        linkedToUser: !!user,
      };

      const docRef = await addDoc(collection(db, 'subscriptionPayments'), paymentRecord);
      setPendingPaymentId(docRef.id);

      // Record discount usage with email (will link userId later)
      if (appliedDiscount) {
        await addDoc(collection(db, "subscription_discount_usages"), {
          discountId: appliedDiscount.id,
          userId: user?.uid || null,
          userEmail: payerEmail,
          tier: tier,
          usedAt: serverTimestamp(),
          discountAmount: discountAmount
        });
      }

      console.log('✅ Subscription payment record saved successfully');
      
      setPaymentStatus('uploaded');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      setPaymentStatus('pending');
    } finally {
      setIsUploading(false);
    }
  };

  // Success Modal
  if (paymentStatus === 'success') {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">ส่งสลิปสำเร็จ!</h2>
            <p className="text-gray-600 mb-4">
              เราได้รับสลิปการโอนเงินของคุณเรียบร้อยแล้ว
            </p>
            
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-center gap-2 text-blue-900 mb-2">
                <Mail className="w-5 h-5" />
                <p className="font-semibold">กรุณาสมัครสมาชิกด้วยอีเมล:</p>
              </div>
              <p className="text-blue-800 font-bold text-lg">{payerEmail}</p>
              <p className="text-blue-700 text-xs mt-2">เพื่อเชื่อมโยงกับการชำระเงินของคุณ</p>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-blue-900 font-semibold mb-2">ขั้นตอนต่อไป:</p>
              <ol className="text-sm text-blue-800 space-y-1 text-left list-decimal list-inside">
                <li>สมัครสมาชิกด้วย email เดียวกัน</li>
                <li>รอ Admin อนุมัติการชำระเงิน</li>
                <li>ยืนยันอีเมลของคุณ</li>
                <li>เข้าใช้งานได้เต็มรูปแบบ</li>
              </ol>
            </div>

            <button
              onClick={() => setShowAuthModal(true)}
              className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Mail className="w-5 h-5" />
              สมัครด้วย Gmail
            </button>
          </div>
        </div>

        {/* Auth Modal */}
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode="register"
          tier={tier}
          requiredEmail={payerEmail}
          pendingPaymentId={pendingPaymentId}
        />
      </>
    );
  }

  const getSEOContent = () => {
    const isProPlus = tier === 'pro_plus_monthly' || tier === 'pro_plus_yearly';
    const isPro = tier === 'pro_monthly' || tier === 'pro_yearly';
    const isYearly = tier === 'pro_yearly' || tier === 'pro_plus_yearly';
    
    if (isProPlus) {
      return {
        title: isYearly ? 'สมัคร Pro+ รายปี - SEO Optimization & Sale Page Builder' : 'สมัคร Pro+ รายเดือน - SEO Optimization & Sale Page Builder',
        description: isYearly 
          ? 'สมัครแพ็กเกจ Pro+ รายปี 15,900฿/ปี (ประหยัด 2 เดือน) พื้นที่ 35GB รองรับนักเรียนไม่จำกัด พร้อม SEO Optimization ทำให้คอร์สติด Google Search และ Sale Page Builder ฟรี สร้างหน้าขายคอร์สได้ง่าย'
          : 'สมัครแพ็กเกจ Pro+ รายเดือน 1,590฿/เดือน พื้นที่ 30GB รองรับนักเรียนไม่จำกัด พร้อม SEO Optimization ทำให้คอร์สติด Google Search และ Sale Page Builder ฟรี สร้างหน้าขายคอร์สได้ง่าย',
        keywords: isYearly
          ? 'Pro+ รายปี, สมัคร Pro+ รายปี, แพ็กเกจ Pro+ รายปี, SEO Optimization, Sale Page Builder, ขายคอร์สออนไลน์, EduMi Pro+, ผู้สอนมืออาชีพ, Google Search คอร์ส, ประหยัด 2 เดือน'
          : 'Pro+ รายเดือน, สมัคร Pro+ รายเดือน, แพ็กเกจ Pro+ รายเดือน, SEO Optimization, Sale Page Builder, ขายคอร์สออนไลน์, EduMi Pro+, ผู้สอนมืออาชีพ, Google Search คอร์ส'
      };
    } else if (isPro) {
      return {
        title: isYearly ? 'สมัคร Pro รายปี - แพลตฟอร์มสอนออนไลน์' : 'สมัคร Pro รายเดือน - แพลตฟอร์มสอนออนไลน์',
        description: isYearly
          ? 'สมัครแพ็กเกจ Pro รายปี 6,900฿/ปี (ประหยัด 2 เดือน) พื้นที่ 25GB รองรับนักเรียนไม่จำกัด สร้างคอร์สไม่จำกัด พร้อม Dashboard และ Analytics'
          : 'สมัครแพ็กเกจ Pro รายเดือน 690฿/เดือน พื้นที่ 20GB รองรับนักเรียนไม่จำกัด สร้างคอร์สไม่จำกัด พร้อม Dashboard และ Analytics',
        keywords: isYearly
          ? 'Pro รายปี, สมัคร Pro รายปี, แพ็กเกจ Pro รายปี, ขายคอร์สออนไลน์, EduMi Pro, ผู้สอนเริ่มต้น, ประหยัด 2 เดือน'
          : 'Pro รายเดือน, สมัคร Pro รายเดือน, แพ็กเกจ Pro รายเดือน, ขายคอร์สออนไลน์, EduMi Pro, ผู้สอนเริ่มต้น'
      };
    }
    
    return {
      title: 'สมัครแพ็กเกจ - EduMi',
      description: 'สมัครแพ็กเกจ EduMi เพื่อเริ่มสอนออนไลน์และขายคอร์สของคุณ',
      keywords: 'สมัคร EduMi, แพลตฟอร์มสอนออนไลน์, ขายคอร์สออนไลน์'
    };
  };

  const seoContent = getSEOContent();

  return (
    <>
      <SEO
        title={seoContent.title}
        description={seoContent.description}
        keywords={seoContent.keywords}
        noindex={false}
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white py-12 px-4">
        <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>ย้อนกลับ</span>
        </button>

        <h1 className="text-3xl md:text-4xl font-bold text-textDark mb-2 text-center">
          {showPaymentForm ? 'ชำระเงิน' : 'เลือกแพ็กเกจ'} {tier === 'pro_monthly' || tier === 'pro_yearly' ? 'Pro' : tier === 'pro_plus_monthly' || tier === 'pro_plus_yearly' ? 'Pro+' : plan.name}
        </h1>
        <p className="text-center text-textLight mb-8">{showPaymentForm ? 'สแกน QR Code หรือโอนเงินผ่าน PromptPay' : 'เลือกระยะเวลาการชำระเงิน'}</p>

        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-8 items-start">
          {/* Payment Form - 3 columns */}
          <div className="w-full lg:col-span-3 bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
            {success ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-textDark mb-3">ชำระเงินสำเร็จ!</h2>
                <p className="text-textLight mb-6">กำลังเปลี่ยนเส้นทางไปหน้าสำเร็จ...</p>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : showPaymentForm ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-textDark">ชำระเงิน</h2>
                  {expiryTime && (
                    <div className="flex items-center gap-2 text-blue-600">
                      <Clock className="w-5 h-5" />
                      <span className="font-semibold">{timeRemaining}</span>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-pink-600 rounded-2xl p-8 text-white mb-6">
                  <div className="text-center mb-6">
                    <img
                      src={generatePromptPayQR()}
                      alt="PromptPay QR Code"
                      className="w-64 h-64 mx-auto bg-white p-4 rounded-xl"
                    />
                  </div>
                  
                  <div className="text-center">
                    <div className="text-sm opacity-90 mb-1">จำนวนเงิน</div>
                    <div className="text-4xl font-bold mb-4">฿{finalPrice.toLocaleString()}</div>
                    <div className="text-sm opacity-90">
                      {billingPeriod === 'yearly' ? 'ชำระรายปี (ฟรี 2 เดือน)' : 'ชำระรายเดือน'}
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">เลขพร้อมเพย์</span>
                      <button
                        onClick={() => copyToClipboard(PROMPTPAY_NUMBER)}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            คัดลอกแล้ว
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            คัดลอก
                          </>
                        )}
                      </button>
                    </div>
                    <div className="text-lg font-bold text-gray-900">{PROMPTPAY_NUMBER}</div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-sm text-gray-600 mb-1">ชื่อบัญชี</div>
                    <div className="text-lg font-bold text-gray-900">{PROMPTPAY_NAME}</div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">เลขอ้างอิง</span>
                      <button
                        onClick={() => copyToClipboard(paymentRef)}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                      >
                        <Copy className="w-4 h-4" />
                        คัดลอก
                      </button>
                    </div>
                    <div className="text-lg font-bold text-gray-900 font-mono">{paymentRef}</div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <div className="font-semibold mb-1">วิธีการชำระเงิน:</div>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>สแกน QR Code หรือโอนเงินผ่าน PromptPay</li>
                        <li>ใส่เลขอ้างอิงในช่องหมายเหตุ</li>
                        <li>อัปโหลดสลิปการโอนเงินด้านล่าง</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t pt-6 mb-6">
                  <h3 className="text-xl font-bold text-textDark mb-4">ข้อมูลติดต่อ</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        อีเมล <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={payerEmail}
                          onChange={(e) => setPayerEmail(e.target.value)}
                          placeholder="example@gmail.com"
                          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none transition-colors ${
                            user ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300 focus:border-blue-500'
                          }`}
                          readOnly={!!user}
                          disabled={!!user}
                          required
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {user ? 'อีเมลจากบัญชีที่ล็อกอินอยู่' : 'ใช้สำหรับเชื่อมโยงกับการชำระเงินและสมัครสมาชิก'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        ชื่อ-นามสกุล (ไม่บังคับ)
                      </label>
                      <input
                        type="text"
                        value={payerName}
                        onChange={(e) => setPayerName(e.target.value)}
                        placeholder="ชื่อของคุณ"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Slip Upload */}
                <div className="border-t pt-6">
                  <h3 className="text-xl font-bold text-textDark mb-4">อัปโหลดสลิปการโอนเงิน</h3>
                  
                  {paymentStatus === 'pending' && (
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleSlipUpload}
                          className="hidden"
                          id="slip-upload"
                        />
                        <label htmlFor="slip-upload" className="cursor-pointer">
                          {slipPreview ? (
                            <div>
                              <img
                                src={slipPreview}
                                alt="Payment Slip Preview"
                                className="max-w-full max-h-64 mx-auto rounded-lg mb-4"
                              />
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSlipFile(null);
                                  setSlipPreview(null);
                                }}
                                className="text-red-600 hover:text-red-700 text-sm"
                              >
                                ลบรูป
                              </button>
                            </div>
                          ) : (
                            <div>
                              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                              <p className="text-gray-700 font-medium mb-2">คลิกเพื่ออัปโหลดสลิป</p>
                              <p className="text-sm text-gray-500">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</p>
                            </div>
                          )}
                        </label>
                      </div>

                      <button
                        onClick={handleSubmitPayment}
                        disabled={!slipFile || isUploading}
                        className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            กำลังอัปโหลด...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            ยืนยันการชำระเงิน
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {paymentStatus === 'uploaded' && (
                    <div className="py-6 md:py-8">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                          <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-green-600" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                          ได้รับการชำระเงินแล้ว!
                        </h3>
                        <p className="text-sm md:text-base text-gray-600 mb-1">
                          เราได้รับสลิปการโอนเงินของคุณเรียบร้อยแล้ว
                        </p>
                        <p className="text-xs md:text-sm text-gray-500">
                          เลขอ้างอิง: <span className="font-mono font-semibold">{paymentRef}</span>
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 md:p-6 border-2 border-blue-200 mb-6">
                        <div className="flex items-start gap-3">
                          <Mail className="w-5 h-5 md:w-6 md:h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-bold text-blue-900 mb-2 text-sm md:text-base">
                              สำคัญ! กรุณาสมัครสมาชิกด้วยอีเมล:
                            </h4>
                            <div className="bg-white rounded-lg p-3 mb-3 border border-blue-200">
                              <p className="text-blue-800 font-bold text-base md:text-lg text-center break-all">{payerEmail}</p>
                            </div>
                            <ul className="space-y-2 text-xs md:text-sm text-blue-800">
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold">1.</span>
                                <span>สมัครสมาชิกด้วย <strong>อีเมลเดียวกัน</strong> เพื่อเชื่อมโยงการชำระเงิน</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold">2.</span>
                                <span>รอ Admin อนุมัติการชำระเงิน (ภายใน 24 ชม.)</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold">3.</span>
                                <span>ยืนยันอีเมลของคุณ</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold">4.</span>
                                <span>เข้าใช้งานได้เต็มรูปแบบ</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 md:p-6 border-2 border-blue-200 mb-6">
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 md:w-6 md:h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-bold text-blue-900 mb-2 text-sm md:text-base">
                              ⏰ ระยะเวลาการตรวจสอบ
                            </h4>
                            <ul className="space-y-2 text-xs md:text-sm text-blue-800">
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600">•</span>
                                <span>ทีมงานจะตรวจสอบภายใน <strong>24 ชั่วโมง</strong></span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600">•</span>
                                <span>คุณจะได้รับอีเมลยืนยันเมื่อการชำระเงินได้รับการอนุมัติ</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600">•</span>
                                <span>สมาชิกจะเริ่มใช้งานได้ทันทีหลังอนุมัติ</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="bg-green-50 rounded-xl p-4 border border-green-200 mb-6">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <h4 className="font-semibold text-green-900 text-sm md:text-base">รายละเอียดการชำระเงิน</h4>
                        </div>
                        <div className="space-y-1 text-xs md:text-sm text-green-800">
                          <div className="flex justify-between">
                            <span>จำนวนเงิน:</span>
                            <span className="font-bold">฿{finalPrice.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>แพ็กเกจ:</span>
                            <span className="font-semibold">{plan?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>รอบชำระ:</span>
                            <span className="font-semibold">{billingPeriod === 'yearly' ? 'รายปี' : 'รายเดือน'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <button
                          onClick={() => navigate('/register')}
                          className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold text-sm md:text-base hover:shadow-xl transition-all flex items-center justify-center gap-2"
                        >
                          <Mail className="w-5 h-5" />
                          สมัครสมาชิก
                        </button>
                        <button
                          onClick={() => navigate('/login')}
                          className="w-full px-4 py-3 bg-white text-gray-700 rounded-xl font-semibold text-sm md:text-base hover:bg-gray-50 transition-all flex items-center justify-center gap-2 border-2 border-gray-200"
                        >
                          เข้าสู่ระบบ (ถ้ามีบัญชีแล้ว)
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg md:text-2xl font-bold text-textDark mb-4 md:mb-6">เลือกระยะเวลาชำระเงิน</h2>

                {/* Billing Period Selection */}
                <div className="mb-4 md:mb-6">
                  <div className="flex items-center gap-2 mb-3 md:mb-4">
                    <Calendar className="w-4 md:w-5 h-4 md:h-5 text-blue-600" />
                    <h3 className="text-sm md:text-lg font-bold text-textDark">รอบการชำระเงิน</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 md:gap-4">
                    <button
                      onClick={() => setBillingPeriod('monthly')}
                      className={`p-3 md:p-4 rounded-lg md:rounded-xl border-2 transition-all ${
                        billingPeriod === 'monthly'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-sm md:text-lg font-bold text-gray-900 mb-0.5 md:mb-1">รายเดือน</div>
                        <div className="text-lg md:text-2xl font-bold text-blue-600 mb-0.5 md:mb-1">฿{plan.price.toLocaleString()}</div>
                        <div className="text-xs md:text-sm text-gray-600">ชำระทุกเดือน</div>
                      </div>
                    </button>

                    <button
                      onClick={() => setBillingPeriod('yearly')}
                      className={`p-3 md:p-4 rounded-lg md:rounded-xl border-2 transition-all relative ${
                        billingPeriod === 'yearly'
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300'
                      }`}
                    >
                      <div className="absolute -top-1.5 md:-top-2 -right-1.5 md:-right-2 bg-green-500 text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-semibold">
                        ฟรี 2 เดือน
                      </div>
                      <div className="text-center">
                        <div className="text-sm md:text-lg font-bold text-gray-900 mb-0.5 md:mb-1">รายปี</div>
                        <div className="text-lg md:text-2xl font-bold text-green-600 mb-0.5 md:mb-1">฿{(plan.price * 10).toLocaleString()}</div>
                        <div className="text-xs md:text-sm text-gray-600">ประหยัด ฿{(plan.price * 2).toLocaleString()}</div>
                      </div>
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="mt-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                {/* Discount Code Section */}
                <div className="mt-4 md:mt-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg md:rounded-xl p-4 md:p-6">
                  <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                    <Tag className="w-4 md:w-6 h-4 md:h-6 text-blue-600" />
                    <h3 className="text-sm md:text-lg font-bold text-textDark">รหัสส่วนลด</h3>
                  </div>
                  
                  {appliedDiscount ? (
                    <div className="bg-green-50 border-2 border-green-200 rounded-lg md:rounded-xl p-3 md:p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 md:gap-3">
                          <div className="w-8 md:w-10 h-8 md:h-10 bg-green-500 rounded-lg flex items-center justify-center">
                            <Check className="w-4 md:w-6 h-4 md:h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm md:text-base font-bold text-green-900">{appliedDiscount.code}</p>
                            <p className="text-xs md:text-sm text-green-700">
                              {appliedDiscount.type === "percentage" 
                                ? `ลด ${appliedDiscount.value}%` 
                                : `ลด ฿${appliedDiscount.value.toLocaleString()}`}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveDiscount}
                          className="p-1.5 md:p-2 hover:bg-green-100 rounded-lg transition-all"
                          title="ลบส่วนลด"
                        >
                          <X className="w-4 md:w-5 h-4 md:h-5 text-green-700" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          placeholder="ใส่รหัสส่วนลด"
                          className="flex-1 px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-blue-500 focus:ring-2 md:focus:ring-4 focus:ring-purple-100 focus:outline-none transition-all"
                          onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                        />
                        <button
                          onClick={handleApplyDiscount}
                          disabled={checkingDiscount || !discountCode.trim()}
                          className="px-4 md:px-6 py-2 md:py-3 text-sm md:text-base bg-gradient-primary text-white rounded-lg md:rounded-xl font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
                        >
                          {checkingDiscount ? "กำลังตรวจสอบ..." : "ใช้"}
                        </button>
                      </div>
                      {discountError && (
                        <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                          <X className="w-4 h-4" />
                          {discountError}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Security Notice */}
                <div className="mt-6 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-xl p-4">
                  <Lock className="w-4 h-4 text-green-600" />
                  <span>การชำระเงินของคุณปลอดภัยและเข้ารหัส</span>
                </div>

                {/* Proceed Button */}
                <button
                  onClick={handleProceedToPayment}
                  disabled={processing}
                  className="w-full mt-4 md:mt-6 bg-gradient-to-r from-blue-600 to-pink-600 text-white py-3 md:py-4 rounded-lg md:rounded-xl font-bold text-base md:text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  ดำเนินการชำระเงิน ฿{finalPrice.toLocaleString()}
                </button>
              </>
            )}
          </div>

          {/* Order Summary - 2 columns */}
          <div className="w-full lg:col-span-2">
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8 lg:sticky lg:top-8">
              <h3 className="text-base md:text-xl font-bold text-textDark mb-4 md:mb-6">สรุปคำสั่งซื้อ</h3>
              
              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-sm md:text-base text-textLight">แพ็กเกจ</span>
                  <span className="text-sm md:text-base font-semibold text-textDark">{plan.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm md:text-base text-textLight">ระยะเวลา</span>
                  <span className="text-sm md:text-base font-semibold text-textDark">{billingPeriod === 'yearly' ? 'รายปี (ฟรี 2 เดือน)' : 'รายเดือน'}</span>
                </div>
                {billingPeriod === 'yearly' && (
                  <div className="flex justify-between items-center text-xs md:text-sm bg-green-50 p-2 md:p-3 rounded-lg">
                    <span className="text-green-700 font-semibold">ส่วนลดรายปี</span>
                    <span className="text-green-700 font-bold">-฿{(plan.price * 2).toLocaleString()}</span>
                  </div>
                )}
                {appliedDiscount && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base text-textLight flex items-center gap-1">
                      <Tag className="w-3 md:w-4 h-3 md:h-4" />
                      ส่วนลด ({appliedDiscount.code})
                    </span>
                    <span className="text-sm md:text-base font-semibold text-green-600">-฿{discountAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t pt-3 md:pt-4 flex justify-between items-center">
                  <span className="text-base md:text-lg font-semibold text-textDark">ยอดรวม</span>
                  <span className="text-xl md:text-2xl font-bold text-primary">฿{finalPrice.toLocaleString()}</span>
                </div>
                {appliedDiscount && (
                  <div className="text-center">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                      <Tag className="w-4 h-4" />
                      ประหยัด ฿{discountAmount.toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-textDark font-semibold mb-3">สิ่งที่ได้รับ:</p>
                <ul className="text-sm text-textDark space-y-2">
                  {(tier === "pro_monthly" || tier === "pro_yearly") && (
                    <>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>พื้นที่จัดเก็บ 20 GB {billingPeriod === "yearly" && <span className="text-blue-600 font-semibold">(+5 GB)</span>}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>ไม่จำกัดนักเรียน</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>สร้างคอร์สได้ไม่จำกัด</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Dashboard และ Analytics</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Auto video processing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>เพิ่มพื้นที่ได้ (Add-on)</span>
                      </li>
                    </>
                  )}
                  {(tier === "pro_plus_monthly" || tier === "pro_plus_yearly") && (
                    <>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>พื้นที่จัดเก็บ 30 GB {billingPeriod === "yearly" && <span className="text-blue-600 font-semibold">(+5 GB)</span>}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>ไม่จำกัดนักเรียน</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>สร้างคอร์สได้ไม่จำกัด</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Dashboard และ Analytics</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Auto video processing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>เพิ่มพื้นที่ได้ (Add-on)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-blue-600 font-semibold">SEO Optimization</span>
                      </li>
                      {billingPeriod === "yearly" && (
                        <>
                          <li className="flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-blue-600 font-semibold">
                              Subdomain ฟรี (Save 1,188฿/ปี)
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-blue-600 font-semibold">
                              Salepage ฟรี 1 หน้า (Save 1,788฿/ปี)
                            </span>
                          </li>
                        </>
                      )}
                    </>
                  )}
                </ul>
              </div>

              <div className="text-xs text-textLight space-y-1">
                <p>✓ ยกเลิกได้ทุกเมื่อ</p>
                <p>✓ ต่ออายุอัตโนมัติ</p>
                <p>✓ อัปเกรด/ดาวน์เกรดได้</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
